import * as React from "react";
import { onKeyDown } from "@utdanningsdirektoratet/utils/uu";
import AnimateHeight from "react-animate-height";
import { cls } from "@utdanningsdirektoratet/utils/cls";
import { isEmpty } from "@utdanningsdirektoratet/utils/string";
import { Icon } from "@utdanningsdirektoratet/icon";
import type { MultiSelectOption } from "./MultiSelect";
import Options from "./Options";
import styles from "./MultiSelect.module.less";

type GroupProps<T> = {
  options: Array<MultiSelectOption<T>>;
  title: string;
  disabled?: boolean;
  expanded?: boolean;
  onChange: (valuesToAdd: Array<T>, valuesToRemove: Array<T>) => void;
  values: Array<T>;
  search: string;
  index: number;
  focusedRef: React.RefObject<HTMLInputElement> | null;
  focusedIndex: string | null;
  id: string;
  isExpanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
};

const Group = <T,>({
  id,
  title,
  isExpanded,
  setExpanded,
  options,
  search,
  onChange,
  focusedRef,
  index,
  focusedIndex,
  disabled,
  expanded = false,
  values,
}: GroupProps<T>): React.ReactElement | null => {
  const hasCheckedOptions = React.useMemo(
    () => values.some((v) => options.map((option) => option.value).indexOf(v) !== -1),
    [options, values]
  );

  const prevExpanded = React.useRef(isExpanded);

  React.useEffect(() => {
    setExpanded((prevState) => ({
      ...prevState,
      [title]: expanded || prevExpanded.current || hasCheckedOptions,
    }));
  }, [expanded, hasCheckedOptions, setExpanded, title]);

  React.useEffect(() => {
    if (expanded) {
      setExpanded((prevState) => ({ ...prevState, [title]: true }));
    } else {
      setExpanded((prevState) => ({
        ...prevState,
        [title]: prevExpanded.current,
      }));
    }
  }, [expanded, setExpanded, title]);

  React.useEffect(() => {
    prevExpanded.current = prevExpanded.current || hasCheckedOptions;
  }, [hasCheckedOptions]);

  if (search && options.filter((option) => option.text.toLowerCase().indexOf(search.toLowerCase()) !== -1).length === 0) return null;

  const onClick = () => {
    if (!disabled) {
      setExpanded((prevState) => {
        prevExpanded.current = !prevState[title];
        return {
          ...prevState,
          [title]: !prevState[title],
        };
      });
    }
  };

  const headingClass = cls({
    [styles.multiSelectGroupheading]: true,
    [styles.multiSelectGroupheadingSelected]: focusedIndex === `${index}`,
  });

  const optionsClass = cls({
    [styles.multiSelectOptions]: true,
    [styles.multiSelectOptionsInGroup]: true,
  });

  const iconClass = cls({
    [styles.multiSelectIcon]: true,
    [styles.multiSelectIconInGroup]: true,
    [styles.multiSelectIconIsOpen]: isExpanded || !isEmpty(search),
    [styles.multiSelectIconDisabled]: disabled,
  });

  return (
    <div className={styles.multiSelectGroup} role="group">
      <div
        className={headingClass}
        ref={focusedIndex === `${index}` ? (focusedRef as React.RefObject<HTMLDivElement>) : null}
        onClick={onClick}
        onKeyDown={onKeyDown(onClick)}
        aria-label={title}
        aria-expanded={isExpanded || !isEmpty(search)}
        aria-controls={`${id}-group-${index}`}
        id={`${id}-${index}`}
      >
        {title}
        <Icon className={iconClass} icon="angle down" />
      </div>
      {options.length !== 0 && (
        <AnimateHeight
          id={`${id}-group-${index}`}
          height={isExpanded || !isEmpty(search) ? "auto" : 0}
          aria-hidden={!isExpanded || isEmpty(search)}
        >
          <div className={optionsClass}>
            <Options
              id={id}
              focusedIndex={focusedIndex}
              focusedRef={focusedRef}
              options={options}
              values={values}
              disabled={disabled}
              onChange={onChange}
              groupTitle={title}
              groupIndex={`${index}`}
              search={search}
            />
          </div>
        </AnimateHeight>
      )}
    </div>
  );
};

export default Group;
